import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import getOpenSourceEditLink from '@modules/utilities/utils/getOpenSourceEditLink';
import { Locale, useTranslation } from '@rbx/intl';
import { Button, Typography, EditOutlinedIcon } from '@rbx/ui';
import React, { FunctionComponent, useMemo } from 'react';
import { TestId } from '@modules/utilities/constants/testConstants';
import { SvgIconOpenSourceEdit } from '@modules/ui/icons/EngineReferenceIcons';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import useOpenSourceEditButtonStyles from './OpenSourceEditButton.styles';

interface OpenSourceEditButtonProps {
  isEmployee: boolean;
  contentDirFilePath: string;
  machineTranslated: boolean;
  engineRevampEnabled?: boolean;
}

export const OpenSourceEditButton: FunctionComponent<OpenSourceEditButtonProps> = ({
  isEmployee,
  contentDirFilePath,
  machineTranslated,
  engineRevampEnabled = false,
}) => {
  const { translate } = useTranslation();
  const { urlLocale: currentUrlLocale } = useCurrentLocale();
  const editLinkLocale = machineTranslated ? currentUrlLocale : Locale.English;
  const editLink = useMemo(
    () => getOpenSourceEditLink(isEmployee, editLinkLocale, contentDirFilePath),
    [contentDirFilePath, isEmployee, editLinkLocale],
  );

  const {
    classes: { btnWrapper, icon, iconRevamp, buttonRevamp },
  } = useOpenSourceEditButtonStyles();

  const trackClick = () => {
    unifiedLoggerClient.logClickEvent({
      eventName: DocsSiteEventType.OpenSourceEditButtonClicked,
      parameters: { source: DocsSiteSource.OpenSourceEditButton },
    });
  };

  return (
    <a
      className={btnWrapper}
      role='button'
      aria-label={translate('Label.Edit') || 'Edit'}
      href={editLink}
      rel='noreferrer'
      tabIndex={-1}
      target='_blank'>
      <Button
        onClick={trackClick}
        color='primaryBrand'
        size='small'
        data-testid={TestId.OpenSourceEditButton}
        variant='text'
        className={engineRevampEnabled ? buttonRevamp : undefined}>
        {engineRevampEnabled ? (
          <React.Fragment>
            <span className={iconRevamp}>
              <SvgIconOpenSourceEdit />
            </span>
            <Typography variant='body1' color='inherit'>
              {translate('Label.Edit') || 'Edit'}
            </Typography>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Typography variant='body1' color='inherit'>
              {translate('Label.Edit') || 'Edit'}
            </Typography>
            <EditOutlinedIcon className={icon} fontSize='inherit' />
          </React.Fragment>
        )}
      </Button>
    </a>
  );
};

export default OpenSourceEditButton;
OpenSourceEditButton.displayName = 'OpenSourceEditButton';
